import videojs from 'video.js';

import './timeline-marker.css';
import TimelineMarkerIcon from './timeline-marker-icon.js';

const _time = new WeakMap();
const _label = new WeakMap();
const _color = new WeakMap();
const _shape = new WeakMap();

const DEFAULT_MARKER_ICON = `<svg viewBox="0 0 10 8" xmlns="http://www.w3.org/2000/svg">
    <path d = "M5.82199 6.81355C5.42431 7.38756 4.57569 7.38756 4.17801 6.81355L0.891225 2.0695C0.431746 1.40629 0.906401 0.500001 1.71322 0.500001L8.28678 0.5C9.0936 0.5 9.56825 1.40629 9.10877 2.06949L5.82199 6.81355Z"/>
</svg >`

const DEFAULT_COLOR = "#d4bf86";

export default class TimelineMarker extends videojs.getComponent('ClickableComponent') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} options
     * @param {number} options.time
     * @param {string} options.label
     */
    constructor(player, options) {
        super(player, options);

        player.ready(() => {
            const { time, label, color, shape } = options;

            //creates a span for the label to exist in
            const labelDiv = document.createElement("span");
            labelDiv.classList.add("mediasite-player__timeline-marker-label");
            this.el().appendChild(labelDiv);

            this.time = time;
            this.label = label;
            this.color = color ?? this.color;
            this.shape = shape ?? this.shape;

            this.setMarkerIcon();            

            this.on('mousedown', this.handleMouseDown);
            this.on('click', this.handleClick);
            this.on(player, 'durationchange', this.handleDurationChange);
        });
    }

    handleMouseDown(event) {
        if (this.player().settings().enableSeeking) {
            event.stopPropagation();
        }
    }

    handleClick() {
        if (this.player().settings().enableSeeking) {
            this.player().currentTime(this.time);
            this.player().trigger('timelinemarkerclicked', this.time);
        }
    }

    handleDurationChange() {
        this.time = this.time;
    }

    set hidden(hidden) {
        if (hidden) {
            this.addClass('vjs-hidden');
        }
        else {
            this.removeClass('vjs-hidden');
        }
    }

    get hidden() {
        return this.hasClass('vjs-hidden');
    }

    set time(time) {
        _time.set(this, time);
        const position = time / this.player().duration();
        this.el().style.setProperty('--timeline-marker-pos', position);
        this.label = this.label;
    }

    get time() {
        return _time.get(this);
    }

    set label(label) {
        //prevent empty string or undefined variables from being set as labels
        if (!label) {
            return;
        }
        _label.set(this, label);
        const el = this.el();
        //modify the label's content and position so that it properly reflects what needs to be said
        //and is fully on screen the while doing it
        const labelEl = el.querySelector(".mediasite-player__timeline-marker-label");

        if (labelEl) {
            labelEl.innerHTML = `${videojs.formatTime(this.time)} - ${label}`;
            this.#sizeLabel(labelEl);
        }

        el.setAttribute('aria-label', label);
    }

    #sizeLabel(labelEl) {
        //grabs the amount of px control of the padding left and converts it to an int
        //used for giving a natural spacing to the label
        let horizontalPadding = window.getComputedStyle(labelEl).paddingLeft;
        if (horizontalPadding) {
            horizontalPadding = parseInt(horizontalPadding, 10);
        } else {
            horizontalPadding = 0;
        }

        //check the bounding rectangle to see where this label is positioned on the screen
        //and compare it to where the video player element is on screen
        //if it is too far left or right, add margin to bring it back on screen
        //postive margin-left to move left, negative margin-left to move right, since margin-right
        //would only move the element if there was something else to the right of it
        const labelBounding = labelEl.getBoundingClientRect();
        const playerBounding = this.player().el().getBoundingClientRect();

        if (labelBounding.left < playerBounding.left) {
            let leftOffset = playerBounding.left - labelBounding.left;
            leftOffset += horizontalPadding;
            labelEl.style.setProperty('margin-left', `${leftOffset}px`);
        }
        if (labelBounding.right > playerBounding.right) {
            let rightOffset = labelBounding.right - playerBounding.right;
            rightOffset += horizontalPadding;
            labelEl.style.setProperty('margin-left', `-${rightOffset}px`);
        }
    }

    get label() {
        return _label.get(this);
    }

    set color(color) {
        let markerColor = color ?? DEFAULT_COLOR;
        _color.set(this, markerColor);
        this.el().style.setProperty('--timeline-marker-color', `${markerColor}`);
    }

    get color() {
        return _color.get(this);
    }

    set shape(shape) {
        if (shape != null && shape.length > 0) {
            _shape.set(this, shape);
        } else {
            _shape.set(this, DEFAULT_MARKER_ICON);
        }
        this.setMarkerIcon();
    }

    get shape() {
        return _shape.get(this);
    }

    setMarkerIcon() {
        const shape = this.shape;

        const children = this.children();
        for (const child of children) {
            this.removeChild(child);
        }

        const customIcon = new TimelineMarkerIcon(this.player(), { name: "TimelineMarkerIcon", shape: shape });
        this.addChild(customIcon);
    }

    createEl(tag, props = {}, attributes = {}) {
        tag = 'div';
        props = Object.assign({
            className: 'mediasite-player__timeline-marker'
        });

        return super.createEl.call(this, tag, props, attributes);
    }
}
